import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  FiClock,
  FiX,
  FiPlus,
  FiMessageSquare,
  FiPaperclip,
  FiSend,
  FiLoader,
} from "react-icons/fi";
import { MdAutoAwesome } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../HelperAxios/axios";
import {
  AI_TOKEN_BALANCE_STORAGE_KEY,
  askAssistantFromPrompt,
  detectTranslationIntent,
  generateBlogDraftFromPrompt,
  generateBlogTranslationDraftFromPrompt,
  getAiBlogDraft,
  hasTranslationKeywords,
  saveAiBlogDraft,
} from "../../services/aiBlogDraftService";

const gradient = "linear-gradient(135deg, #701951, #8f2d6b)";
const AI_CHAT_HISTORY_STORAGE_KEY = "ai_assistant_chat_history_v1";

const starterAssistantMessage =
  "Select Ask mode for normal chat, or Agent mode to apply changes to your blog draft.";

const buildEmptyChat = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: "New chat",
  updatedAt: Date.now(),
  messages: [{ role: "assistant", content: starterAssistantMessage }],
});

const formatTimeAgo = (timestamp) => {
  const now = Date.now();
  const diffMs = Math.max(0, now - Number(timestamp || 0));
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export default function AIAssistantSlide() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [assistantMode, setAssistantMode] = useState("agent");
  const [remainingTokens, setRemainingTokens] = useState(() => {
    const raw = sessionStorage.getItem(AI_TOKEN_BALANCE_STORAGE_KEY);
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  });
  const [chats, setChats] = useState(() => {
    try {
      const raw = sessionStorage.getItem(AI_CHAT_HISTORY_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch (error) {
      return [buildEmptyChat()];
    }
    return [buildEmptyChat()];
  });
  const [currentChatId, setCurrentChatId] = useState(() => {
    try {
      const raw = sessionStorage.getItem(AI_CHAT_HISTORY_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed[0]?.id) return parsed[0].id;
    } catch (error) {
      return null;
    }
    return null;
  });
  const fileRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      sessionStorage.setItem(AI_CHAT_HISTORY_STORAGE_KEY, JSON.stringify(chats));
    }, 120);
    return () => window.clearTimeout(timeoutId);
  }, [chats]);

  useEffect(() => {
    if (remainingTokens === null || remainingTokens === undefined) return;
    sessionStorage.setItem(AI_TOKEN_BALANCE_STORAGE_KEY, String(remainingTokens));
  }, [remainingTokens]);

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get("subscription/my")
      .then((res) => {
        if (!mounted) return;
        const next = Number(res?.data?.aiToken);
        if (Number.isFinite(next)) {
          setRemainingTokens(next);
        }
      })
      .catch(() => {
        // Keep last known value from storage when endpoint is unavailable.
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!currentChatId && chats[0]?.id) {
      setCurrentChatId(chats[0].id);
    }
  }, [chats, currentChatId]);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (event) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [open]);

  const activeChat = chats.find((chat) => chat.id === currentChatId) || chats[0];
  const chatMessages = activeChat?.messages || [];
  const visibleChatMessages = chatMessages.slice(-40);

  const updateActiveChat = (nextMessages, nextTitle) => {
    const chatId = activeChat?.id;
    if (!chatId) return;
    setChats((prev) => {
      const next = prev.map((chat) =>
        chat.id === chatId
          ? {
              ...chat,
              messages: nextMessages,
              updatedAt: Date.now(),
              title: nextTitle || chat.title || "New chat",
            }
          : chat,
      );
      return [...next].sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
    });
  };

  const createNewChat = () => {
    const newChat = buildEmptyChat();
    setChats((prev) => [newChat, ...prev]);
    setCurrentChatId(newChat.id);
    setView("chat");
    setResultMessage("");
    resetComposer();
  };

  const resetComposer = () => {
    setMessage("");
    setFiles([]);
  };

  const handleGenerateBlogDraft = async () => {
    let nextMessages = chatMessages;
    try {
      const userText = String(message || "").trim();
      if (!userText) return;
      setCreating(true);
      setResultMessage("");
      nextMessages = [...chatMessages, { role: "user", content: userText }];
      const nextTitle =
        activeChat?.title === "New chat" ? userText.slice(0, 50) : activeChat?.title;
      updateActiveChat(nextMessages, nextTitle);
      if (assistantMode === "agent") {
        const translationIntent = detectTranslationIntent(userText);
        if (!translationIntent && hasTranslationKeywords(userText)) {
          const helpMessage =
            "I detected a translation request, but I need both target language and source article id. Example: make persian translation for article 76";
          updateActiveChat([
            ...nextMessages,
            {
              role: "assistant",
              content: helpMessage,
            },
          ]);
          setResultMessage(helpMessage);
          resetComposer();
          return;
        }
        if (translationIntent) {
          const { draft, postId } = await generateBlogTranslationDraftFromPrompt({
            prompt: userText,
            conversation: nextMessages,
          });
          updateActiveChat([
            ...nextMessages,
            {
              role: "assistant",
              content: `Translation draft generated for blog ${draft.sourceId} in ${String(draft.language).toUpperCase()}. I loaded it into the Add Translation form.`,
            },
          ]);
          setResultMessage("Agent mode: translation draft generated and loaded.");
          navigate(`/add-post-translation/${postId}`);
          resetComposer();
          return;
        }

        const { draft, remainingAiToken } = await generateBlogDraftFromPrompt({
          prompt: userText,
          conversation: nextMessages,
          baseDraft: getAiBlogDraft(),
        });
        if (remainingAiToken !== null) {
          setRemainingTokens(remainingAiToken);
        }
        saveAiBlogDraft(draft);
        window.dispatchEvent(new CustomEvent("ai-blog-draft-updated"));
        updateActiveChat([
          ...nextMessages,
          {
            role: "assistant",
            content: `Draft generated: "${draft.title}". I loaded it into the Create Post form. Tell me more changes anytime.`,
          },
        ]);
        setResultMessage("Agent mode: draft generated and loaded into Create Post.");
        navigate("/add-blog-post");
      } else {
        const { answer, remainingAiToken } = await askAssistantFromPrompt({
          prompt: userText,
          conversation: nextMessages,
          baseDraft: getAiBlogDraft(),
        });
        if (remainingAiToken !== null) {
          setRemainingTokens(remainingAiToken);
        }
        updateActiveChat([
          ...nextMessages,
          {
            role: "assistant",
            content: answer,
          },
        ]);
        setResultMessage("Ask mode: replied in chat only.");
      }
      resetComposer();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to create blog draft.";
      const errText = String(apiMessage);
      setResultMessage(errText);
      updateActiveChat([...nextMessages, { role: "assistant", content: errText }]);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {!open && (
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            right: 20,
            bottom: 20,
            zIndex: 1200,
            width: 48,
            height: 48,
            color: "#fff",
            background: gradient,
            "&:hover": { opacity: 0.95, background: gradient },
          }}
        >
          <MdAutoAwesome size={18} />
        </IconButton>
      )}

      <Box
        ref={panelRef}
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: { xs: "100%", sm: "390px" },
          maxWidth: "100%",
          height: "100vh",
          bgcolor: "background.paper",
          borderLeft: "1px solid",
          borderColor: "divider",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.25s ease",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            color: "#fff",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: "rgba(255,255,255,0.2)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <MdAutoAwesome size={16} />
            </Box>
            <Box>
              <Typography fontSize={13} fontWeight={700}>
                AI Assistant
              </Typography>
              <Typography fontSize={11} sx={{ opacity: 0.8 }}>
                {remainingTokens === null
                  ? "Tokens left: --"
                  : `Tokens left: ${remainingTokens}`}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              onClick={() => setView(view === "chat" ? "history" : "chat")}
              sx={{ color: "#fff" }}
            >
              <FiClock size={16} />
            </IconButton>
            <IconButton onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
              <FiX size={16} />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {view === "history" ? (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 0.5, mb: 1 }}
              >
                <Typography fontSize={12} color="text.secondary">
                  Recent chats
                </Typography>
                <Button
                  size="small"
                  startIcon={<FiPlus size={14} />}
                  onClick={createNewChat}
                >
                  New
                </Button>
              </Stack>
              <List dense sx={{ p: 0 }}>
                {chats.map((item) => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => {
                      setCurrentChatId(item.id);
                      setView("chat");
                    }}
                    sx={{
                      borderRadius: "10px",
                      mb: 0.5,
                      bgcolor:
                        item.id === activeChat?.id ? "action.selected" : "transparent",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.2}
                      alignItems="flex-start"
                      sx={{ width: "100%" }}
                    >
                      <FiMessageSquare
                        size={16}
                        style={{ marginTop: "2px", opacity: 0.7 }}
                      />
                      <ListItemText
                        primary={item.title || "New chat"}
                        secondary={formatTimeAgo(item.updatedAt)}
                        primaryTypographyProps={{ fontSize: 14, noWrap: true }}
                        secondaryTypographyProps={{ fontSize: 11 }}
                      />
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
            </>
          ) : (
            <Stack spacing={1.2}>
              {visibleChatMessages.map((item, idx) => {
                const isUser = item.role === "user";
                return (
                  <Box
                    key={`${item.role}-${idx}`}
                    sx={{
                      alignSelf: isUser ? "flex-end" : "flex-start",
                      maxWidth: "88%",
                      px: 1.25,
                      py: 0.9,
                      borderRadius: "12px",
                      bgcolor: isUser ? "primary.main" : "action.hover",
                      color: isUser ? "primary.contrastText" : "text.primary",
                      whiteSpace: "pre-wrap",
                      fontSize: 13,
                    }}
                  >
                    {item.content}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Box sx={{ borderTop: "1px solid", borderColor: "divider", p: 1.5 }}>
          {!!resultMessage && (
            <Typography
              fontSize={12}
              color="text.secondary"
              sx={{ mb: 1, px: 0.5 }}
            >
              {resultMessage}
            </Typography>
          )}
          {!!files.length && (
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
              {files.map((f, i) => (
                <Chip
                  key={`${f.name}-${i}`}
                  size="small"
                  label={f.name}
                  onDelete={() =>
                    setFiles(files.filter((_, idx) => idx !== i))
                  }
                />
              ))}
            </Stack>
          )}
          <Paper variant="outlined" sx={{ borderRadius: "12px", p: 1 }}>
            <TextField
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!message.trim() || creating) return;
                  handleGenerateBlogDraft();
                }
              }}
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              placeholder="Message AI assistant..."
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton onClick={() => fileRef.current?.click()}>
                  <FiPaperclip size={16} />
                </IconButton>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (!e.target.files) return;
                    setFiles([...files, ...Array.from(e.target.files)]);
                  }}
                />
                <Button
                  size="small"
                  variant={assistantMode === "ask" ? "contained" : "text"}
                  onClick={() => setAssistantMode("ask")}
                >
                  Ask
                </Button>
                <Button
                  size="small"
                  variant={assistantMode === "agent" ? "contained" : "text"}
                  onClick={() => setAssistantMode("agent")}
                >
                  Agent
                </Button>
              </Stack>
              <IconButton
                onClick={handleGenerateBlogDraft}
                disabled={!message.trim() || creating}
                sx={{
                  width: 34,
                  height: 34,
                  color: "#fff",
                  background: gradient,
                  "&:hover": { background: gradient, opacity: 0.95 },
                  "&.Mui-disabled": { opacity: 0.45, color: "#fff" },
                }}
              >
                {creating ? <FiLoader size={16} /> : <FiSend size={16} />}
              </IconButton>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </>
  );
}
